import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Alert, CircularProgress, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { FormDialog } from "components/dialogs/Dialogs";
import CargoActualsForm from "components/cargoaction/CargoActualsForm";

import { ChildProps, ChildPropsFromArray } from "types/commons";
import { CallType } from "__generated__/enums";
import {
	StatusChangerCargoActionMeasurementChecksDocument,
	StatusChangerCargoActionMeasurementChecksQuery,
} from "__generated__/view/statuses/graphql/queries.generated";
import { SaveCargoMeasurementsDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { CargoActualsOutput } from "view/cargos/utils/schema";

import "../../styling/styling.scss";
import VolumeUnitSelect from "view/cargos/components/selects/VolumeUnitSelect";
import WeightUnitSelect from "view/cargos/components/selects/WeightUnitSelect";

interface StatusChangerMeasurementFieldProps {
	voyageId?: number;
	callId?: number;
	cargoId?: number;
	cargoActionType?: CallType | null;
	onChange: (valid: boolean) => void;
}

type Check = ChildPropsFromArray<StatusChangerCargoActionMeasurementChecksQuery, "cargoActionMeasurementChecks">;
type Action = ChildProps<Check, "action">;

const StatusChangerMeasurementField = ({ voyageId, callId, cargoId, cargoActionType, onChange }: StatusChangerMeasurementFieldProps) => {

	const { data, refetch, loading } = useQuery(StatusChangerCargoActionMeasurementChecksDocument, {
		variables: {
			voyageId,
			callId,
			cargoId,
			cargoActionType,
			onlyLoading: false,
		},
	});

	const checks = data?.cargoActionMeasurementChecks || [];

	useEffect(() => {
		onChange(!!data && checks.every(c => c.checked));
	}, [ data?.cargoActionMeasurementChecks ]);

	return (
		<div className="cargo-action-measurement-checks">
			{checks.length > 0 && !checks.every(c => c.checked) && (
				<Alert
					severity="warning"
					action={
						<Tooltip title={intl.formatMessage({ id: "UPDATES_REFRESH" })} placement="top">
							<IconButton onClick={() => refetch()} size="small">
								<RefreshIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					}>
					{intl.formatMessage({ id: "VOLUME_WEIGHT_QUANTITIES_MUST_BE_FILLED_IN" })}
				</Alert>
			)}

			<div className="cam-checks">
				{loading && (
					<CircularProgress size={18} />
				)}

				{checks.map((c) => (
					<div className="cam-check" key={c.action.id}>
						<IconButton size="small" disabled>
							{c.checked ? (
								<CheckIcon fontSize="small" color="success" />
							) : (
								<ClearIcon fontSize="small" color="error" />
							)}
						</IconButton>
						<span className="description">
							{`${c.action.location?.code || c.action.location?.description} - ${c.action.cargo.cargoNumber || c.action.cargo.containerNumber || c.action.cargo.product?.name}`}
						</span>

						<span className="actions">


							{!c.checked && (
								<React.Fragment>
									<MeasurementsDialog
										action={c.action}
										onChange={refetch} />

									<Tooltip title={intl.formatMessage({ id: "EMAIL_GO_TO_VOYAGE" })} placement="top">
										<IconButton onClick={() => window.open(`/view/skipper/voyage/${c.action.cargo.voyage?.id}?ship=${c.action.cargo.voyage?.ship.id}&call=${c.action.call?.id}`)} size="small">
											<ChevronRightIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</React.Fragment>
							)}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

interface MeasurementsDialogProps {
	action: Action;
	onChange: () => void;
}

const MeasurementsDialog = ({ action, onChange }: MeasurementsDialogProps) => {

	const [ open, setOpen ] = useState(false);

	const [ saveMeasurements, { loading } ] = useMutation(SaveCargoMeasurementsDocument);

	const save = (form: CargoActualsOutput) => {
		saveMeasurements({
			variables: {
				input: form.measurements,
				actionId: action.id,
				cargoId: action.cargo.id,
			},
		}).then(() => {
			setOpen(false);
			onChange();
		});
	};

	return (
		<React.Fragment>
			<IconButton onClick={() => setOpen(true)} size="small">
				<EditIcon fontSize="small" />
			</IconButton>

			<FormDialog
				open={open}
				onClose={() => setOpen(false)}
				title={(
					intl.formatMessage({ id: "X_ACTION_FOR_Y_PRODUCT_AT_Z_LOCATION" })
						.replace("_X_", intl.formatMessage({ id: `LOAD_OR_DISCHARGE_${action.type}` }))
						.replace("_Y_", (action.cargo.cargoNumber ? `${intl.formatMessage({ id: "CARGO" })} ${action.cargo.cargoNumber}` : null) || action.cargo.product?.name || action.cargo.containerNumber || "")
						.replace("_Z_", action.location?.description || "")
				)}
				form="cargoActionForm"
				maxWidth="md"
				loading={loading}>
				<CargoActualsForm
					action={action}
					onSave={save} />
			</FormDialog>
		</React.Fragment>
	);
};

export default StatusChangerMeasurementField;
